import type { CommandEnvelope } from "./commands";
import type { EventEnvelope } from "./events";

// ─── RPC Query Types ──────────────────────────────────────────────────

export type QueryType =
  | { type: "me" }
  | { type: "user"; userId: string }
  | { type: "userSessions"; userId: string }
  | { type: "userEvents"; userId: string; beforeTs?: number; limit?: number }
  | { type: "terminateSession"; userId: string; targetSessionId: string }
  | { type: "listUsers" }
  | { type: "listUnits" }
  | {
      type: "audit";
      search?: string;
      eventType?: string;
      userId?: string;
      entityId?: string;
      beforeTs?: number;
      limit?: number;
    }
  | { type: "auditTypes" }
  | { type: "searchUnits"; search?: string }
  | {
      type: "listCases";
      search?: string;
      triage?: string;
      status?: string;
      traumaType?: string;
      hospital?: string;
      includeArchived?: boolean;
      sortKey?: string;
      sortDir?: "asc" | "desc";
      page?: number;
      limit?: number;
    }
  | {
      type: "listPatients";
      search?: string;
      sortKey?: string;
      sortDir?: "asc" | "desc";
      page?: number;
      limit?: number;
      includeArchived?: boolean;
    }
  | { type: "getPatient"; patientId: string }
  | { type: "syncCase"; caseId: string }
  | { type: "syncEntity"; entityId: string }
  | {
      type: "insights";
      hospitalIds?: string[];
      unitIds?: string[];
      startTime?: number;
      endTime?: number;
      granularity?: "day" | "week" | "month";
    }
  | {
      type: "chart";
      spec: ChartSpec;
      hospitalIds?: string[];
      unitIds?: string[];
      startTime?: number;
      endTime?: number;
      granularity?: "day" | "week" | "month";
    }
  | { type: "canDelete"; entityType: DeletableEntityType; entityId: string }
  | { type: "deleteEntity"; entityType: DeletableEntityType; entityId: string }
  | { type: "listMyMessages"; beforeTs?: number; limit?: number };

export type DeletableEntityType = "user" | "patient" | "case" | "hospital" | "region" | "unit";

export interface CanDeleteResult {
  deletable: boolean;
  reason?: string;
}

export type ChartType = "bar" | "stackedBar" | "line" | "pie" | "heatmap";
export type ChartDimension =
  | "time"
  | "region"
  | "hospital"
  | "unit"
  | "triage"
  | "trauma"
  | "hourOfDay"
  | "dayOfWeek";
export type ChartMetric = "cases" | "deaths" | "mortalityPct";

export interface ChartSpec {
  chartType: ChartType;
  dimension: ChartDimension;
  breakdown?: Exclude<ChartDimension, "trauma">;
  metric: ChartMetric;
}

export interface ChartResultRow {
  key: string;
  keyLabel: string;
  series?: string;
  seriesLabel?: string;
  cases: number;
  deaths: number;
}

export interface ChartResult {
  rows: ChartResultRow[];
}

export interface InsightsResult {
  totalCases: number;
  totalDeaths: number;
  byTriage: Array<{ category: string; count: number }>;
  byTrauma: Array<{ type: string; cases: number; deaths: number }>;
  byRegion: Array<{ id: string; name: string; cases: number; deaths: number }>;
  byHospital: Array<{
    id: string;
    name: string;
    cases: number;
    deaths: number;
  }>;
  byUnit: Array<{ id: string; name: string; cases: number; deaths: number }>;
  byMonth: Array<{ period: string; cases: number; deaths: number }>;
}

export interface UserInfo {
  id: string;
  email: string;
  name: string | null;
  picture: string | null;
  role: string;
  hospitals: string[];
  units: string[];
  regions: string[];
  subordinateUnitIds: string[];
  canBroadcastToOwnHospital: boolean;
  canBroadcastToOwnUnit: boolean;
}

export interface MessageItem {
  id: string;
  senderId: string;
  senderName: string | null;
  senderEmail: string;
  audienceType: string;
  audienceHospitalId: string | null;
  audienceHospitalName: string | null;
  audienceUnitIds: string[] | null;
  body: string;
  createdAt: number;
}

export interface MessageListResult {
  messages: MessageItem[];
  hasMore: boolean;
}

export interface UserListItem {
  id: string;
  email: string;
  name: string | null;
  picture: string | null;
  role: string;
  firstLoginAt: number | null;
  lastLoginAt: number | null;
  inviteSentAt: number | null;
  hospitals: string[];
  units: string[];
  regions: string[];
}

export interface UnitListItem {
  id: string;
  name: string | null;
  archived: number;
  parentUnitId: string | null;
  advisors: Array<{ id: string; name: string | null; email: string }>;
}

export interface SessionInfo {
  id: string;
  userId: string;
  createdAt: number;
  lastSeenAt: number;
  isCurrent: boolean;
  destroyed: boolean;
}

export interface AuditEvent {
  id: string;
  type: string;
  entityId: string;
  payload: Record<string, unknown>;
  userId: string;
  sessionId: string;
  clientTimestamp: number;
  serverTimestamp: number;
  userName?: string;
}

// ─── Case & Patient List Results ─────────────────────────────────────

export interface CaseListItem {
  id: string;
  patientId: string;
  number: string | null;
  hospitalId: string | null;
  unitId: string | null;
  eventTime: number | null;
  eventLocation: string | null;
  arrivalTime: number | null;
  triageCategory: string | null;
  role: string | null;
  medicalFacility: string | null;
  transportType: string | null;
  traumaTypes: string[] | null;
  traumaPenetrating: number | null;
  traumaNonPenetrating: number | null;
  conditionTypes: string[] | null;
  cbrn: string[] | null;
  deathTime: number | null;
  dischargeTime: number | null;
  dischargeDestination: string | null;
  dischargeHospitalId: string | null;
  dischargeUnitId: string | null;
  evacuationCategory: string | null;
  dischargeTransportType: string | null;
  dischargePosition: string | null;
  escortDoctor: number | null;
  escortNurse: number | null;
  diagnosis: string | null;
  notes: string | null;
  medicName: string | null;
  medicFacility: string | null;
  medicFacilityName: string;
  medicRole: string | null;
  archived: number;
  updatedAt: number;
  // Enriched by server
  patientName: string;
  patientDateOfBirth: string | null;
  patientIdNumber: string | null;
  patientSex: string | null;
  patientBloodType: string | null;
  patientRhFactor: string | null;
  patientAllergyStatus: string | null;
  patientAllergyDetails: string | null;
  patientMilitaryRank: string | null;
  hospitalName: string;
  unitName: string;
}

export interface CaseListResult {
  cases: CaseListItem[];
  page: number;
  totalPages: number;
  totalCount: number;
}

export interface PatientListItem {
  id: string;
  name: string | null;
  dateOfBirth: string | null;
  sex: string | null;
  bloodType: string | null;
  rhFactor: string | null;
  allergyStatus: string | null;
  allergyDetails: string | null;
  militaryRank: string | null;
  unitId: string | null;
  unitName: string;
  caseCount: number;
  updatedAt: number;
}

export interface PatientListResult {
  patients: PatientListItem[];
  page: number;
  totalPages: number;
  totalCount: number;
}

export interface PatientDetail {
  id: string;
  name: string | null;
  dateOfBirth: string | null;
  sex: string | null;
  bloodType: string | null;
  rhFactor: string | null;
  allergyStatus: string | null;
  allergyDetails: string | null;
  militaryRank: string | null;
  unitId: string | null;
  unitName: string;
  personalDataErased: number;
  archived: number;
  idNumber: string | null;
  cases: Array<{
    id: string;
    number: string | null;
    triageCategory: string | null;
    eventTime: number | null;
    eventLocation: string | null;
  }>;
}

// ─── Client → Server ──────────────────────────────────────────────────

// ─── Merkle Tree (2-level: 256 top-level groups × 256 sub-buckets) ───

/** Level-2 (leaf) bucket: 4-char hex prefix, hash of contained entities */
export interface MerkleBucket {
  prefix: string;
  hash: string;
  entityCount: number;
}

/** Level-1 (top) group: 2-char hex prefix, hash of its sub-buckets */
export interface MerkleGroup {
  prefix: string;
  hash: string;
}

export type ClientMessage =
  | { tag: "hello"; version: number; appVersion?: number }
  | { tag: "commands"; envelopes: CommandEnvelope[] }
  | { tag: "merkle"; root: string; groups: MerkleGroup[] }
  | { tag: "drilldown"; prefixes: string[] }
  | { tag: "resync"; entityIds: string[] }
  | { tag: "subscribe"; caseIds: string[] }
  | { tag: "query"; id: string; query: QueryType }
  | { tag: "ping" };

// ─── Server → Client ──────────────────────────────────────────────────

export type ServerMessage =
  | {
      tag: "hello";
      version: number;
      minAppVersion?: number;
      userId: string;
      sessionId: string;
      recentCaseIds?: string[];
    }
  | { tag: "event"; envelope: EventEnvelope }
  | { tag: "events"; envelopes: EventEnvelope[] }
  | { tag: "drilldown"; buckets: MerkleBucket[] }
  | { tag: "divergent"; entities: Array<{ entityId: string; hash: string }> }
  | { tag: "synced" }
  | { tag: "batchAck"; commandIds: string[] }
  | {
      tag: "batchError";
      commandIds: string[];
      failedIndex: number;
      code: string;
      message: string;
    }
  | { tag: "response"; id: string; result: unknown }
  | { tag: "queryError"; id: string; code: string; message: string }
  | { tag: "invalidate"; resources: string[] }
  | { tag: "pong" };

// Protocol version - increment when breaking changes are made
export const PROTOCOL_VERSION = 6;
